import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { NotificationPreferencesService } from '../../../../notifications/services/notification-preferences.service';
import { NotificationStore } from '../../../../notifications/services/notification.store';
import { RealtimeService } from '../../../../notifications/services/realtime.service';
import { PushService } from '../../../../services/push-service';
import { SessaoService } from '../../../../services/sessao.service';
import { AuthService } from '../../../../auth/services/auth.service';
import {
  NotificationType,
  NotificationCategory,
  RADIUS_OPTIONS,
  NOTIFICATION_CATEGORIES,
  ProximitySubscriptionConfig,
  filterToBackendRecognizedTypes,
} from '../../../../notifications/models/notification.model';

@Component({
  selector: 'app-notification-settings-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatSnackBarModule,
  ],
  templateUrl: './notification-settings-dialog.html',
  styleUrl: './notification-settings-dialog.scss',
})
export class NotificationSettingsDialog {
  private readonly dialogRef = inject(MatDialogRef<NotificationSettingsDialog>);
  private readonly preferencesService = inject(NotificationPreferencesService);
  private readonly realtimeService = inject(RealtimeService);
  private readonly pushService = inject(PushService);
  private readonly sessaoService = inject(SessaoService);
  private readonly authService = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);
  readonly store = inject(NotificationStore);

  readonly categories = NOTIFICATION_CATEGORIES;
  readonly radiusOptions = RADIUS_OPTIONS;
  readonly SENTINELA = NotificationCategory.SENTINELA;

  readonly pushHabilitado = signal(this.preferencesService.pushPreferences().pushEnabled);
  readonly enabledTypes = signal([...this.preferencesService.pushPreferences().enabledTypes]);
  readonly raioKm = signal(this.preferencesService.pushPreferences().proximityConfig.radiusKm);
  readonly somHabilitado = signal(this.preferencesService.pushPreferences().soundEnabled);
  readonly vibracaoHabilitada = signal(this.preferencesService.pushPreferences().vibrationEnabled);

  readonly activatingPush = signal(false);

  private readonly disabledCategories = signal<NotificationCategory[]>(
    this.deriveDisabledCategories()
  );

  private deriveDisabledCategories(): NotificationCategory[] {
    const currentEnabledTypes = this.preferencesService.pushPreferences().enabledTypes;
    const disabled: NotificationCategory[] = [];

    for (const cat of NOTIFICATION_CATEGORIES) {
      const hasAnyEnabled = cat.types.some(t => currentEnabledTypes.includes(t.value));
      if (!hasAnyEnabled) {
        disabled.push(cat.category);
      }
    }

    return disabled;
  }

  isCategoryEnabled(category: NotificationCategory): boolean {
    return !this.disabledCategories().includes(category);
  }

  isTypeEnabled(type: NotificationType): boolean {
    return this.enabledTypes().includes(type);
  }

  onPushHabilitadoChange(checked: boolean): void {
    this.pushHabilitado.set(checked);
  }

  onCategoryToggle(category: NotificationCategory, enabled: boolean): void {
    const catConfig = this.categories.find(c => c.category === category);
    if (!catConfig) return;

    if (enabled) {
      this.disabledCategories.update(cats => cats.filter(c => c !== category));
      const types = catConfig.types.map(t => t.value);
      this.enabledTypes.update(current => [...new Set([...current, ...types])]);
    } else {
      this.disabledCategories.update(cats => [...cats, category]);
      const types = catConfig.types.map(t => t.value);
      this.enabledTypes.update(current => current.filter(t => !types.includes(t)));
    }
  }

  onTypeToggle(type: NotificationType): void {
    const current = this.enabledTypes();
    if (current.includes(type)) {
      this.enabledTypes.set(current.filter(t => t !== type));
    } else {
      this.enabledTypes.set([...current, type]);
    }
  }

  onRaioChange(value: number): void {
    this.raioKm.set(value);
  }

  onSomChange(checked: boolean): void {
    this.somHabilitado.set(checked);
  }

  onVibracaoChange(checked: boolean): void {
    this.vibracaoHabilitada.set(checked);
  }

  async onActivatePush(): Promise<void> {
    this.activatingPush.set(true);
    try {
      const sub = await this.pushService.requestPermission();
      if (sub) {
        this.snackBar.open('Push ativado com sucesso', 'Fechar', { duration: 3000 });
      } else {
        this.snackBar.open('Não foi possível ativar push. Verifique as permissões do navegador.', 'Fechar', { duration: 4000 });
      }
    } finally {
      this.activatingPush.set(false);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const sentinelaTypes = this.enabledTypes().filter(t =>
      [
        NotificationType.ALERTA_DESAPARECIDO,
        NotificationType.ALERTA_PROCURADO,
        NotificationType.ALERTA_VEICULO,
        NotificationType.ALERTA_PROPRIETARIO_PROCURADO,
      ].includes(t)
    );
    const sentinelaEnabled = this.isCategoryEnabled(NotificationCategory.SENTINELA);

    const proximityConfig: ProximitySubscriptionConfig = {
      enabled: sentinelaEnabled && this.pushHabilitado(),
      radiusKm: this.raioKm(),
      alertTypes: sentinelaTypes,
      soundEnabled: this.somHabilitado(),
      vibrationEnabled: this.vibracaoHabilitada(),
    };

    const wasEnabled = this.preferencesService.config().enabled;

    this.preferencesService.updatePushPreferences({
      pushEnabled: this.pushHabilitado(),
      enabledTypes: this.enabledTypes(),
      proximityConfig,
      soundEnabled: this.somHabilitado(),
      vibrationEnabled: this.vibracaoHabilitada(),
    });

    if (proximityConfig.enabled && !wasEnabled) {
      this.realtimeService.connect();
    }
    if (!proximityConfig.enabled && wasEnabled) {
      this.realtimeService.unsubscribeProximity();
    }
    if (proximityConfig.enabled && wasEnabled) {
      this.realtimeService.unsubscribeProximity();
      this.realtimeService.subscribeProximity(proximityConfig);
    }

    const payloadSessaoNotificacao = {
      matricula: this.authService.user()?.matricula ?? '',
      contexto: 'NEXUS_WEB',
      salvoEm: new Date().toISOString(),
      configuracao: {
        habilitado: this.pushHabilitado(),
        raioKm: proximityConfig.radiusKm,
        tiposAlerta: filterToBackendRecognizedTypes(this.enabledTypes() as unknown as string[]),
      },
    };

    this.sessaoService.salvarConfigNotificacao(payloadSessaoNotificacao).subscribe({
      next: () => {
        this.snackBar.open('Configurações salvas com sucesso', 'Fechar', {
          duration: 3000
        });
      },
      error: (error) => {
        console.error('[NotificationSettingsDialog] Falha ao sincronizar com servidor:', error);
        this.snackBar.open(
          'Configurações salvas localmente. Sincronização com servidor pendente.',
          'Fechar',
          { duration: 4000 }
        );
      },
    });

    this.dialogRef.close(proximityConfig);
  }
}
